import type { MixedModeConfig } from '../../types';

interface MixedModeConfigSectionProps {
  mixedMode: boolean;
  config: MixedModeConfig;
  onToggleAllRandom: (enabled: boolean) => void;
  onConfigChange: (config: Partial<MixedModeConfig>) => void;
  onMixedModeChange: (enabled: boolean) => void;
  isPro: boolean;
  canUseMixedModeConfig: (param: keyof MixedModeConfig) => boolean;
}

export function MixedModeConfigSection({
  mixedMode,
  config,
  onToggleAllRandom,
  onConfigChange,
  onMixedModeChange,
  isPro,
  canUseMixedModeConfig,
}: MixedModeConfigSectionProps): JSX.Element {

  // When user manually toggles an individual config
  const handleIndividualToggle = (key: keyof MixedModeConfig, value: boolean) => {
    // Update the individual config first
    onConfigChange({ [key]: value });

    // Calculate the new state after this change
    const newConfig = { ...config, [key]: value };

    // Check if all 6 flags are now true
    const allEnabled = Object.values(newConfig).every(v => v === true);

    // Sync the master toggle with the individual states
    // If all are enabled and master is off, turn master on
    // If not all enabled and master is on, turn master off
    if (allEnabled && !mixedMode) {
      onMixedModeChange(true);
    } else if (!allEnabled && mixedMode) {
      onMixedModeChange(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* All Random Master Toggle */}
      <label className="flex items-center gap-3 pb-2 border-b border-gray-200">
        <input
          type="checkbox"
          checked={mixedMode}
          onChange={(e) => onToggleAllRandom(e.target.checked)}
          disabled={!isPro}
          className="w-5 h-5 text-purple-600 rounded"
        />
        <div className="flex-1">
          <span className="text-sm font-semibold text-purple-700">
            All Random (Mixed Mode)
            {!isPro && <span className="pro-icon ml-1"></span>}
          </span>
          <p className="text-xs text-gray-500">Randomize all parameters simultaneously</p>
        </div>
      </label>

      {/* Individual Parameter Toggles */}
      <div className="space-y-2 pl-4">
        {/* Color */}
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={config.randomizeColor}
            onChange={(e) => handleIndividualToggle('randomizeColor', e.target.checked)}
            disabled={!canUseMixedModeConfig('randomizeColor')}
            className="w-4 h-4 text-sync-blue rounded"
          />
          <span className="text-sm">
            Randomize Color
            {!canUseMixedModeConfig('randomizeColor') && <span className="pro-icon ml-1"></span>}
          </span>
        </label>

        {/* Movement Style */}
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={config.randomizeMovementStyle}
            onChange={(e) => handleIndividualToggle('randomizeMovementStyle', e.target.checked)}
            disabled={!canUseMixedModeConfig('randomizeMovementStyle')}
            className="w-4 h-4 text-sync-blue rounded"
          />
          <span className="text-sm">
            Randomize Movement Style
            {!canUseMixedModeConfig('randomizeMovementStyle') && <span className="pro-icon ml-1"></span>}
          </span>
        </label>

        {/* Direction */}
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={config.randomizeDirection}
            onChange={(e) => handleIndividualToggle('randomizeDirection', e.target.checked)}
            disabled={!canUseMixedModeConfig('randomizeDirection')}
            className="w-4 h-4 text-sync-blue rounded"
          />
          <span className="text-sm">
            Randomize Direction
            {!canUseMixedModeConfig('randomizeDirection') && <span className="pro-icon ml-1"></span>}
          </span>
        </label>

        {/* Background */}
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={config.randomizeBackground}
            onChange={(e) => handleIndividualToggle('randomizeBackground', e.target.checked)}
            disabled={!canUseMixedModeConfig('randomizeBackground')}
            className="w-4 h-4 text-sync-blue rounded"
          />
          <span className="text-sm">
            Randomize Background
            {!canUseMixedModeConfig('randomizeBackground') && <span className="pro-icon ml-1"></span>}
          </span>
        </label>

        {/* Shape */}
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={config.randomizeShape}
            onChange={(e) => handleIndividualToggle('randomizeShape', e.target.checked)}
            disabled={!canUseMixedModeConfig('randomizeShape')}
            className="w-4 h-4 text-sync-blue rounded"
          />
          <span className="text-sm">
            Randomize Shape
            {!canUseMixedModeConfig('randomizeShape') && <span className="pro-icon ml-1"></span>}
          </span>
        </label>

        {/* Sounds */}
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={config.randomizeSounds}
            onChange={(e) => handleIndividualToggle('randomizeSounds', e.target.checked)}
            disabled={!canUseMixedModeConfig('randomizeSounds')}
            className="w-4 h-4 text-sync-blue rounded"
          />
          <span className="text-sm">
            Randomize Sounds
            {!canUseMixedModeConfig('randomizeSounds') && <span className="pro-icon ml-1"></span>}
          </span>
        </label>
      </div>
    </div>
  );
}
